import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';
import AppHeader from '../Header/AppHeader';
import './NotesJournal.css';

function NotesJournal() {
  const { t, i18n } = useTranslation();
  const isGerman = i18n.language === 'de';
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showFeedbackPrompt, setShowFeedbackPrompt] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [selectedFrequency, setSelectedFrequency] = useState('');
  const [filterFrequency, setFilterFrequency] = useState('');
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' or 'calendar'
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [formData, setFormData] = useState({
    frequency_id: '',
    content: '',
    mood: '',
  });

  useEffect(() => {
    loadData();
    loadSubscriptionStatus();
  }, [filterFrequency]);

  const loadSubscriptionStatus = async () => {
    try {
      const data = await apiClient.getSubscriptionStatus();
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error loading subscription status:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [notesData, freqData] = await Promise.all([
        apiClient.getNotes(filterFrequency || null),
        apiClient.getFrequencies(),
      ]);
      setNotes(notesData);
      setFrequencies(freqData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingNote(null);
    setFormData({ frequency_id: selectedFrequency, content: '', mood: '' });
    setShowModal(true);
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setFormData({
      frequency_id: note.frequency_id,
      content: note.content,
      mood: note.mood || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm(t('journal.deleteConfirmMessage'))) {
      return;
    }

    try {
      await apiClient.deleteNote(noteId);
      setNotes(notes.filter((n) => n.id !== noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
      alert(t('journal.errorDeleting'));
    }
  };

  const handleSave = async () => {
    if (!formData.frequency_id || !formData.content.trim()) {
      alert(t('journal.fillRequired'));
      return;
    }

    try {
      if (editingNote) {
        const updated = await apiClient.updateNote(editingNote.id, {
          content: formData.content,
          mood: formData.mood || null,
        });
        setNotes(notes.map((n) => (n.id === updated.id ? updated : n)));
        setShowModal(false);
        setFormData({ frequency_id: '', content: '', mood: '' });
      } else {
        const created = await apiClient.createNote(formData);
        setNotes([created, ...notes]);
        setShowModal(false);
        setFormData({ frequency_id: '', content: '', mood: '' });
        // Show feedback prompt for new entries only
        setShowFeedbackPrompt(true);
      }
    } catch (error) {
      console.error('Error saving note:', error);
      alert(t('journal.errorSaving'));
    }
  };

  const handleGetFeedback = () => {
    setShowFeedbackPrompt(false);
    navigate('/chat');
  };

  const handleSkipFeedback = () => {
    setShowFeedbackPrompt(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleGoToAdmin = () => {
    navigate('/admin');
  };

  const handleGoToHistory = () => {
    navigate('/history');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(isGerman ? 'de-DE' : 'en-US', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(isGerman ? 'de-DE' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(isGerman ? 'de-DE' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Group notes by date
  const groupNotesByDate = () => {
    const grouped = {};
    notes.forEach((note) => {
      const dateKey = formatDateShort(note.created_at);
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          fullDate: formatDate(note.created_at),
          notes: [],
        };
      }
      grouped[dateKey].notes.push(note);
    });
    return grouped;
  };

  const groupedNotes = groupNotesByDate();
  const dateKeys = Object.keys(groupedNotes).sort((a, b) => {
    const dateA = new Date(a.split('.').reverse().join('-'));
    const dateB = new Date(b.split('.').reverse().join('-'));
    return dateB - dateA;
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>{t('journal.loading')}</p>
      </div>
    );
  }

  return (
    <>
      <AppHeader subscriptionStatus={subscriptionStatus} />
      <div className="dashboard-container notes-dashboard">
        <main className="dashboard-main notes-main">
        <div className="journal-header">
          <button onClick={handleCreateNew} className="btn-create-note" data-testid="create-note-button">
            ✍️ {t('journal.newEntry')}
          </button>

          <div className="journal-controls">
            <div className="filter-group">
              <select
                value={filterFrequency}
                onChange={(e) => setFilterFrequency(e.target.value)}
                data-testid="filter-select"
              >
                <option value="">📂 {t('journal.allFrequencies')}</option>
                {frequencies.map((freq) => (
                  <option key={freq.id} value={freq.id}>
                    {freq.icon} {freq.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {notes.length === 0 ? (
          <div className="empty-state journal-empty">
            <div className="empty-icon">📔</div>
            <p>{t('journal.emptyTitle')}</p>
            <p className="empty-state-hint">
              {t('journal.emptyHint')}
            </p>
          </div>
        ) : (
          <div className="journal-timeline">
            {dateKeys.map((dateKey) => {
              const dayData = groupedNotes[dateKey];
              return (
                <div key={dateKey} className="journal-day" data-testid={`journal-day-${dateKey}`}>
                  <div className="day-header">
                    <div className="day-date">
                      <div className="day-date-main">{dayData.fullDate}</div>
                      <div className="day-date-sub">{dayData.notes.length} {dayData.notes.length === 1 ? t('journal.entry') : t('journal.entries')}</div>
                    </div>
                  </div>

                  <div className="day-entries">
                    {dayData.notes.map((note) => (
                      <div key={note.id} className="journal-entry" data-testid={`note-card-${note.id}`}>
                        <div className="entry-time">{formatTime(note.created_at)}</div>
                        
                        <div className="entry-content-wrapper">
                          <div className="entry-header">
                            <div className="entry-frequency" style={{ borderLeftColor: note.frequency_color }}>
                              <span className="entry-freq-icon" style={{ color: note.frequency_color }}>
                                {note.frequency_icon}
                              </span>
                              <span className="entry-freq-name">{note.frequency_name}</span>
                            </div>
                            <div className="entry-actions">
                              <button
                                onClick={() => handleEdit(note)}
                                className="entry-action-btn"
                                data-testid={`edit-note-${note.id}`}
                                title={t('common.edit')}
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => handleDelete(note.id)}
                                className="entry-action-btn delete"
                                data-testid={`delete-note-${note.id}`}
                                title={t('common.delete')}
                              >
                                🗑️
                              </button>
                            </div>
                          </div>

                          {note.mood && (
                            <div className="entry-mood">
                              💭 {note.mood}
                            </div>
                          )}

                          <div className="entry-content">{note.content}</div>

                          {note.updated_at !== note.created_at && (
                            <div className="entry-edited">
                              ✎ {t('journal.edited')}: {formatTime(note.updated_at)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Modal */}
      {showModal && (
        <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-container journal-modal">
            <div className="modal-header">
              <h2 className="modal-title">
                {editingNote ? `✏️ ${t('journal.editEntry')}` : `✍️ ${t('journal.newJournalEntry')}`}
              </h2>
              <button onClick={() => setShowModal(false)} className="modal-close">
                ×
              </button>
            </div>

            <div className="modal-form">
              <div className="journal-modal-date">
                📅 {new Date().toLocaleDateString(isGerman ? 'de-DE' : 'en-US', {
                  weekday: 'long',
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              </div>

              <div className="form-group">
                <label htmlFor="frequency">{t('journal.frequencyLabel')} *</label>
                <select
                  id="frequency"
                  value={formData.frequency_id}
                  onChange={(e) => setFormData({ ...formData, frequency_id: e.target.value })}
                  disabled={!!editingNote}
                  data-testid="frequency-select"
                  required
                >
                  <option value="">{t('journal.selectFrequency')}</option>
                  {frequencies.map((freq) => (
                    <option key={freq.id} value={freq.id}>
                      {freq.icon} {freq.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="mood">💭 {t('journal.moodLabel')}</label>
                <input
                  id="mood"
                  type="text"
                  value={formData.mood}
                  onChange={(e) => setFormData({ ...formData, mood: e.target.value })}
                  placeholder={t('journal.moodPlaceholder')}
                  maxLength={50}
                  data-testid="mood-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="content">📝 {t('journal.experienceLabel')} *</label>
                <textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder={t('journal.experiencePlaceholder')}
                  rows={10}
                  maxLength={2000}
                  required
                  data-testid="content-textarea"
                />
                <span className="char-count">{formData.content.length} / 2000 {t('journal.characters')}</span>
              </div>

              <div className="modal-actions">
                <button onClick={() => setShowModal(false)} className="btn-modal-cancel" data-testid="cancel-button">
                  {t('common.cancel')}
                </button>
                <button onClick={handleSave} className="btn-modal-save" data-testid="save-button">
                  💾 {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Prompt */}
      {showFeedbackPrompt && (
        <div className="modal-overlay" data-testid="feedback-prompt-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-body">
              <div className="feedback-prompt-icon">✨</div>
              <h3 className="feedback-prompt-title">{t('journal.entrySaved')}</h3>
              <p className="feedback-prompt-text">
                {t('journal.feedbackPrompt')}
              </p>
              <div className="modal-actions">
                <button 
                  onClick={handleSkipFeedback} 
                  className="btn-modal-cancel"
                  data-testid="skip-feedback-button"
                >
                  {t('journal.notNow')}
                </button>
                <button 
                  onClick={handleGetFeedback} 
                  className="btn-modal-save"
                  data-testid="get-feedback-button"
                >
                  💬 {t('journal.getFeedback')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}

export default NotesJournal;
